import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, Form, Spinner, Alert } from 'react-bootstrap';
import { X, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../../../axios';
import apiError from '../../../error';
import '../../settings/deepfake/deepfake.css';
import MiniVoicePreview from './mini-voice-preview';
import './select-voice-modal.css';

const SelectVoiceModal = ({ show, handleClose, onSelectVoice, vishingVoiceSelectionId, currentVoiceId, campaignTemplateId, onVishingVoiceSelectionCreated }) => {
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applyLoading, setApplyLoading] = useState(false);
    const [selectedVoiceId, setSelectedVoiceId] = useState(null);
    const [activePlayerId, setActivePlayerId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const loadVoices = useCallback(() => {
        setLoading(true);
        axios
            .get('/api/deepfake/list-voice')
            .then((res) => {
                const list = res.data?.data?.voices || res.data?.voices || [];
                setVoices(Array.isArray(list) ? list : []);
                setLoading(false);
            })
            .catch((err) => {
                setVoices([]);
                setLoading(false);
                if (err?.response || err?.request) {
                    apiError(err);
                }
            });
    }, []);

    useEffect(() => {
        if (show) {
            setSelectedVoiceId(null);
            setActivePlayerId(null);
            setSearchQuery('');
            setApplyLoading(false);
            loadVoices();
        }
    }, [show, loadVoices]);

    const showSingleVoiceNotice =
        !loading &&
        voices.length === 1 &&
        currentVoiceId != null &&
        String(currentVoiceId) === String(voices[0]?.voiceId ?? voices[0]?.id ?? '');

    const filteredVoices = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) {
            return voices;
        }
        return voices.filter((v) => {
            const name = (v.name || '').toLowerCase();
            const gender = (v.gender || '').toLowerCase();
            const desc = (v.description || '').toLowerCase();
            return name.includes(q) || gender.includes(q) || desc.includes(q);
        });
    }, [voices, searchQuery]);

    const selectedVoice = useMemo(
        () =>
            voices.find(
                (v) => String(v.voiceId ?? v.id ?? '') === String(selectedVoiceId)
            ) || null,
        [voices, selectedVoiceId]
    );

    const handleApply = async () => {
        if (!selectedVoice) {
            handleClose();
            return;
        }

        const resolvedVoiceId = selectedVoice.voiceId ?? selectedVoice.id;

        const payload = {
            voiceId: resolvedVoiceId,
            id: resolvedVoiceId,
            name: selectedVoice.name,
            gender: selectedVoice.gender,
            description: selectedVoice.description,
            audioUrl: selectedVoice.audioUrl || selectedVoice.previewUrl,
        };

        setApplyLoading(true);
        try {
            // Vishing only: lazily create the record if it doesn't exist yet, then PATCH
            let resolvedSelectionId = vishingVoiceSelectionId;
            const hasExistingId = resolvedSelectionId != null && String(resolvedSelectionId).trim() !== '';

            if (!hasExistingId) {
                // Lazy creation — POST to create a new record linked to this campaign template
                const createRes = await axios.post('/api/vishing-voice-selection/', {
                    campaignTemplateId,
                    isDeepfakeEnabled: true,
                    voiceId: resolvedVoiceId,
                });
                resolvedSelectionId = createRes.data.vishingVoiceSelectionId;
                if (onVishingVoiceSelectionCreated) {
                    onVishingVoiceSelectionCreated(resolvedSelectionId);
                }
            } else {
                // Already exists — PATCH with the chosen voice
                await axios.patch(`/api/vishing-voice-selection/${resolvedSelectionId}`, {
                    isDeepfakeEnabled: true,
                    voiceId: resolvedVoiceId,
                });
            }
        } catch (err) {
            apiError(err);
            setApplyLoading(false);
            return;
        }

        setApplyLoading(false);

        if (onSelectVoice) {
            onSelectVoice(payload);
        }
        handleClose();
    };

    const handleModalClose = () => {
        setActivePlayerId(null);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleModalClose} centered className="add-voice-modal select-voice-modal" size="lg">
            <Modal.Header closeButton={false} className="add-voice-modal-header">
                <div className="add-voice-modal-title-wrap">
                    <h5 className="mb-1">Select a voice</h5>
                    <p className="mb-0">Choose  from admin-configured voice profiles</p>
                </div>
                <button type="button" className="add-voice-close-btn" onClick={handleModalClose} aria-label="Close modal">
                    <X size={18} />
                </button>
            </Modal.Header>

            <Modal.Body className="add-voice-modal-body">


                <div className="select-voice-search-wrap">
                    <label className="select-voice-search" htmlFor="select-voice-search-input">
                        <span className="select-voice-search__icon" aria-hidden>
                            <Search size={18} strokeWidth={2} />
                        </span>
                        <Form.Control
                            id="select-voice-search-input"
                            type="text"
                            autoComplete="off"
                            placeholder="Search by name, gender, or description…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="select-voice-search__input"
                        />
                        {searchQuery ? (
                            <button
                                type="button"
                                className="select-voice-search__clear"
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear search"
                            >
                                <X size={16} />
                            </button>
                        ) : null}
                    </label>
                </div>
                {/* {showSingleVoiceNotice ? (
                    <Alert variant="warning" className="select-voice-modal__single-voice-notice mb-3">
                        This campaign already uses the only voice profile available. To pick a different voice,
                        add another profile first — use <strong>Add New Voice</strong> on the voice step, or go to{' '}
                        <strong>Settings → AI Voice Manager</strong>. Then open this dialog again.
                    </Alert>
                ) : null} */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" size="sm" variant="primary" className="mr-2" />
                        <span className="text-muted">Loading voices…</span>
                    </div>
                ) : (
                    <div className="select-voice-table-wrap">
                        <Table responsive hover className="select-voice-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Voice profile</th>
                                    <th>Gender</th>
                                    <th>Preview</th>
                                    <th className="select-voice-action-cell text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!filteredVoices.length ? (
                                    <tr>
                                        <td colSpan={4} className="select-voice-empty text-center text-muted py-4">
                                            {searchQuery.trim()
                                                ? 'No voices match your search.'
                                                : 'No voices available.'}
                                        </td>
                                    </tr>
                                ) : null}
                                {filteredVoices.map((voice, index) => {
                                    const id = voice.voiceId ?? voice.id ?? `row-${index}`;
                                    const audioUrl = voice.audioUrl || voice.previewUrl;
                                    const isAlreadyAssigned =
                                        currentVoiceId != null &&
                                        String(currentVoiceId) === String(id);
                                    const soleLocked = showSingleVoiceNotice;
                                    const radioDisabled = isAlreadyAssigned;
                                    const radioChecked =
                                        soleLocked && isAlreadyAssigned
                                            ? true
                                            : String(selectedVoiceId) === String(id);
                                    return (
                                        <tr key={String(id)}>
                                            <td>
                                                <span className="select-voice-profile-name">{voice.name || '—'}</span>
                                            </td>
                                            <td>{voice.gender ?? '—'}</td>
                                            <td className="select-voice-preview-cell">
                                                {audioUrl ? (
                                                    <MiniVoicePreview
                                                        src={audioUrl}
                                                        playerId={String(id)}
                                                        activePlayerId={activePlayerId}
                                                        setActivePlayerId={setActivePlayerId}
                                                    />
                                                ) : (
                                                    <span className="text-muted small">No preview</span>
                                                )}
                                            </td>
                                            <td className="select-voice-action-cell text-center">
                                                <Form.Check
                                                    type="radio"
                                                    name="select-voice-profile"
                                                    id={`select-voice-${id}`}
                                                    className="select-voice-action-radio"
                                                    aria-label={`Select voice ${voice.name || ''}`}
                                                    checked={radioChecked}
                                                    disabled={radioDisabled}
                                                    onChange={() => {
                                                        if (!radioDisabled) {
                                                            setSelectedVoiceId(id);
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="add-voice-modal-footer">
                <Button variant="secondary" onClick={handleModalClose} disabled={applyLoading}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    disabled={
                        applyLoading ||
                        showSingleVoiceNotice ||
                        !selectedVoiceId
                    }
                    onClick={handleApply}
                >
                    {applyLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="mr-2" />
                            Saving…
                        </>
                    ) : (
                        'Select Voice'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SelectVoiceModal;
